import { useState, useEffect } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import styles from './PWAPrompt.module.css';

const isIOS       = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isInstalled = window.navigator.standalone === true
  || window.matchMedia('(display-mode: standalone)').matches;

const DISMISS_KEY = 'pwa_install_dismissed';
function wasDismissed() {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    return ts && Date.now() - Number(ts) < 7 * 24 * 60 * 60 * 1000;
  } catch { return false; }
}
function markDismissed() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
}

export default function PWAPrompt() {
  const [installPrompt, setInstallPrompt] = useState(() => window.__installPrompt ?? null);
  const [show, setShow]                   = useState(false);
  const [mode, setMode]                   = useState('');

  const { needRefresh: [needRefresh], updateServiceWorker } = useRegisterSW({
    onRegistered(r) { r && setInterval(() => r.update(), 60 * 60 * 1000); },
  });

  useEffect(() => {
    if (needRefresh) { setMode('update'); setShow(true); }
  }, [needRefresh]);

  // Chrome/Android: the event may have fired before this component mounted —
  // main.jsx captures it globally. We listen for the custom re-dispatch too
  // in case the component mounted in time.
  useEffect(() => {
    if (isInstalled || wasDismissed()) return;

    // Already captured before mount
    if (window.__installPrompt) {
      setInstallPrompt(window.__installPrompt);
      setMode('chrome');
      setShow(true);
      return;
    }

    const onReady = () => {
      setInstallPrompt(window.__installPrompt);
      setMode('chrome');
      setShow(true);
    };
    window.addEventListener('installpromptready', onReady);
    return () => window.removeEventListener('installpromptready', onReady);
  }, []);

  // iOS: manual instructions
  useEffect(() => {
    if (!isIOS || isInstalled || wasDismissed()) return;
    const t = setTimeout(() => { setMode('ios'); setShow(true); }, 2000);
    return () => clearTimeout(t);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') { window.__installPrompt = null; setShow(false); }
  };

  const handleDismiss = () => { markDismissed(); setShow(false); };

  if (!show) return null;

  return (
    <div className={styles.root}>
      {mode === 'update' && (
        <div className={styles.banner}>
          <span className={styles.text}>Nueva versión disponible.</span>
          <button className={styles.btnPrimary} onClick={() => updateServiceWorker(true)}>
            Actualizar ahora
          </button>
        </div>
      )}

      {mode === 'chrome' && (
        <div className={styles.banner}>
          <span className={styles.text}>
            Instala el POS para abrirlo sin navegador, como una app normal.
          </span>
          <button className={styles.btnPrimary} onClick={handleInstall}>Instalar</button>
          <button className={styles.btnGhost} onClick={handleDismiss}>Ahora no</button>
        </div>
      )}

      {mode === 'ios' && (
        <div className={styles.bannerIos}>
          <div className={styles.iosRow}>
            <span className={styles.iosIcon}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 2v10M6 6l4-4 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M4 13v4h12v-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span className={styles.iosText}>
              Para instalar: toca <strong>Compartir</strong>{' '}
              <svg width="13" height="13" viewBox="0 0 20 20" fill="none" style={{verticalAlign:'middle',display:'inline-block'}}>
                <path d="M10 2v10M6 6l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M4 13v4h12v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {' '}y luego <strong>«Agregar a pantalla de inicio»</strong>
            </span>
            <button className={styles.iosClose} onClick={handleDismiss} aria-label="Cerrar">✕</button>
          </div>
        </div>
      )}
    </div>
  );
}
